import config from '../config';

// Reads Usercentrics consent for a spec ({ serviceIds } or { categorySlug }).
// Returns one of:
//   'granted' — UC (or its persisted state) explicitly allows the service/category
//   'denied'  — UC explicitly refuses it, or UC is unreachable/erroring
//   'pending' — UC hasn't answered yet (e.g. first visit, banner not yet actioned).
//               Callers that must not silently drop data should queue and retry
//               once UC settles, rather than treating this the same as a refusal.
// Implicitly granted when UC is disabled.
export function getUsercentricsConsentState (spec = {}) {
    if (!config.get('enableUsercentrics')) {
        return 'granted';
    }

    if (typeof window === 'undefined') {
        return 'denied';
    }

    try {
        const uc = window.UC_UI;
        if (uc && typeof uc.getServicesBaseInfo === 'function') {
            const services = uc.getServicesBaseInfo();
            // UC v2 returns a sync array; UC v3 returns a Promise — fall through.
            if (Array.isArray(services)) {
                const ids = spec.serviceIds || [];
                if (ids.length) {
                    const match = services.find(s => s && ids.indexOf(s.id) !== -1);
                    // No match, or UC hasn't populated this service's consent yet — pending,
                    // not denied, so the caller doesn't drop data before UC has really answered.
                    if (!match || !match.consent || typeof match.consent.status !== 'boolean') {
                        return 'pending';
                    }
                    return match.consent.status ? 'granted' : 'denied';
                }
                if (spec.categorySlug) {
                    const match = services.find(s => s && s.categorySlug === spec.categorySlug);
                    if (!match || !match.consent || typeof match.consent.status !== 'boolean') {
                        return 'pending';
                    }
                    return match.consent.status ? 'granted' : 'denied';
                }
                return 'denied';
            }
        }

        if (typeof window.__otrl_uc_persistedConsent === 'function') {
            const persisted = window.__otrl_uc_persistedConsent(spec);
            if (persisted === true) { return 'granted'; }
            if (persisted === false) { return 'denied'; }
            return 'pending'; // null: nothing persisted yet — UC hasn't been answered
        }
    } catch (e) {
        // deny-by-default
    }

    return 'denied';
}

// Implicitly granted when UC is disabled; denied by default when it can't be read
// (including while UC is still pending — see getUsercentricsConsentState above).
export function isUsercentricsConsentGranted (spec = {}) {
    return getUsercentricsConsentState(spec) === 'granted';
}

// Sync gate for app-side dataLayer pushes. Mirrors the GTM gate in
// build/templates/<brand>/index.html.ejs so events are not pushed before the
// user grants consent for the configured GTM service.
export function getGtmConsentState () {
    const serviceId = config.get('usercentricsGtmServiceId');
    const categorySlug = config.get('usercentricsGtmCategory');
    const spec = serviceId
        ? { serviceIds: [serviceId] }
        : { categorySlug };

    return getUsercentricsConsentState(spec);
}

export function isGtmConsentGranted () {
    return getGtmConsentState() === 'granted';
}

export default { isGtmConsentGranted, isUsercentricsConsentGranted, getGtmConsentState, getUsercentricsConsentState };
